import { useIntl } from '@/intl'
import { CardControl, type CardProps, CardTrailing, TravelRoute } from '@/scopes/prices/components/TransportSelectorLegacy/CardLegacy'
import { airlineCodesMap, AirlineIcon, Icon, ListItem, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'

import { identity } from 'ramda'
import * as React from 'react'

import { styled } from 'styled-components'

const Remark = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
  ${Typography.modVariant('body-xs')}
`

const InfoItem = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  margin-right: ${tk('spacing_md')};
`

const AirlineWrapper = styled(SpaceBetween)`
  display: inline-flex;
  align-items: end;
`

const StyledAirlineIcon = styled(AirlineIcon)`
  height: 20px;
  width: 20px;
`

const InfoBlock = styled.div`
  margin-top: ${tk('spacing_xs')}!important;
  flex-wrap: wrap;

  ${Icon} {
    margin-right: ${tk('spacing_2xs')};
  }
`

export function FlightCard({
  id,
  arrivalDate,
  departureDate,
  route,
  airline,
  luggage,
  flightType,
  price,
  isSelected,
  remark,
  setSelected = identity,
}: CardProps) {
  const intl = useIntl()

  const flightTypeLabel
    = flightType === 'standard'
      ? 'prices_transport_selector_flight_type_info_standard_value'
      : 'prices_transport_selector_flight_type_info_flexible_value'

  return (
    <ListItem.Root>
      <CardControl id={id} onClick={setSelected} isSelected={isSelected} />
      <ListItem.Content>
        <SpaceBetween direction="y" spacing={tk('spacing_2xs')}>
          <ListItem.Label>{`${departureDate.time} - ${arrivalDate.time}`}</ListItem.Label>
          <TravelRoute {...route} />
          {airline && (
            <AirlineWrapper direction="x" spacing={tk('spacing_2xs')}>
              <StyledAirlineIcon glyph={airlineCodesMap[airline.id]} size="md" />
              <Typography $variant="body-sm">{airline.name}</Typography>
            </AirlineWrapper>
          )}
          {luggage && flightType && (
            <InfoBlock>
              {luggage.map(luggageItem => (
                <InfoItem key={luggageItem.type}>
                  <Icon glyph={luggageItem.type === 'hold' ? 'IconLuggage' : 'IconBackpackCheck'} />
                  <Typography $variant="body-sm">
                    {luggageItem.type === 'hold' && `${luggageItem.weight} kg`}
                    {luggageItem.type === 'hand'
                    && (luggageItem.weight > 0
                      ? `${luggageItem.weight} kg`
                      : intl.formatMessage({ id: 'prices_transport_selector_no-hand-luggage' }))}
                  </Typography>
                </InfoItem>
              ))}
              <InfoItem>
                <Icon glyph={`${flightType === 'standard' ? 'IconAirplane' : 'IconAirplaneFlexible'}`} />
                <Typography $variant="body-sm">{intl.formatMessage({ id: flightTypeLabel })}</Typography>
              </InfoItem>
            </InfoBlock>
          )}
          {remark && <Remark>{remark}</Remark>}
        </SpaceBetween>
      </ListItem.Content>
      <CardTrailing price={price} />
    </ListItem.Root>
  )
}

const CardWrapper = styled.div`
  display: flex;
  align-items: center;

  background: ${tk('color_semantic_bg')};
  border: solid 2px ${tk('color_semantic_border_active')};
  border-radius: ${tk('radius_large')};
  box-shadow: ${tk('shadow_md_bottom')};
  padding: ${tk('spacing_md')};
`

export function FlightCardMobile({
  departureDate,
  arrivalDate,
  route,
  airline,
  luggage,
  flightType,
  price,
  remark,
}: CardProps) {
  const intl = useIntl()

  const flightTypeLabel
    = flightType === 'standard'
      ? 'prices_transport_selector_flight_type_info_standard_value'
      : 'prices_transport_selector_flight_type_info_flexible_value'

  return (
    <CardWrapper>
      <ListItem.Content>
        <SpaceBetween direction="y" spacing={tk('spacing_2xs')}>
          <ListItem.Label>{`${departureDate.time} - ${arrivalDate.time}`}</ListItem.Label>
          <TravelRoute {...route} />
          {airline && (
            <AirlineWrapper direction="x" spacing={tk('spacing_2xs')}>
              <StyledAirlineIcon glyph={airlineCodesMap[airline.id]} size="md" />
              <Typography $variant="body-sm">{airline.name}</Typography>
            </AirlineWrapper>
          )}
          {luggage && flightType && (
            <InfoBlock>
              {luggage.map(luggageItem => (
                <InfoItem key={luggageItem.type}>
                  <Icon glyph={luggageItem.type === 'hold' ? 'IconLuggage' : 'IconBackpackCheck'} />
                  <Typography $variant="body-sm">
                    {luggageItem.type === 'hold' && `${luggageItem.weight} kg`}
                    {luggageItem.type === 'hand'
                    && (luggageItem.weight > 0
                      ? `${luggageItem.weight} kg`
                      : intl.formatMessage({ id: 'prices_transport_selector_no-hand-luggage' }))}
                  </Typography>
                </InfoItem>
              ))}
              <InfoItem>
                <Icon glyph={`${flightType === 'standard' ? 'IconAirplane' : 'IconAirplaneFlexible'}`} />
                <Typography $variant="body-sm">{intl.formatMessage({ id: flightTypeLabel })}</Typography>
              </InfoItem>
            </InfoBlock>
          )}
          {remark && <Remark>{remark}</Remark>}
        </SpaceBetween>
      </ListItem.Content>
      <CardTrailing price={price} />
    </CardWrapper>
  )
}
